import React, { useEffect, useState } from "react";
import { useAuth } from '../../AuthContext';
import {
  TextField,
  Typography,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Snackbar,
  Alert,
  Divider,
  Fade,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import debounce from "lodash.debounce";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const API_URL = process.env.REACT_APP_API_URL;

function AddressByPincode({ onUpdate }) {
  const { userId } = useAuth();
  const [pincode, setPincode] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [country, setCountry] = useState("");
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchingPinData, setFetchingPinData] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  
  
  
      useEffect(() => {
          let timer;
        
          if (isLoading) {
            setLoadingSeconds(0); // reset timer
            timer = setInterval(() => {
              setLoadingSeconds(prev => prev + 1);
            }, 1000);
          } else {
            clearInterval(timer);
            setLoadingSeconds(0); // optional reset after done
          }
        
          return () => clearInterval(timer); // cleanup on unmount or loading = false
        }, [isLoading]);

  useEffect(() => {
    const fetchUserAddress = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user/address/?user_id=${userId}`);
        const data = response.data;

        const isComplete = ["address", "pincode", "city", "state", "country"].every(
          (key) => data[key] !== null && data[key] !== ""
        );

        if (isComplete) {
          setAddress(data.address);
          setPincode(data.pincode.toString());
          setDistrict(data.city);
          setState(data.state);
          setCountry(data.country);
          setEditing(false);
        } else {
          setEditing(true);
        }
      } catch (err) {
        setError("Failed to load user address.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserAddress();
  }, [userId]);

  const fetchAddressFromPincode = debounce(async (pin) => {
    setFetchingPinData(true);
    try {
      const response = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      const data = response.data[0];

      if (data.Status === "Success") {
        const po = data.PostOffice[0];
        setDistrict(po.District);
        setState(po.State);
        setCountry(po.Country);
        setError("");
      } else {
        setError("Invalid pincode.");
        setDistrict("");
        setState("");
        setCountry("");
      }
    } catch (err) {
      setError("Error fetching data from pincode API.");
    } finally {
      setFetchingPinData(false);
    }
  }, 600);

  const handlePincodeChange = (e) => {
    const val = e.target.value;
    setPincode(val);
    if (val.length === 6 && /^\d+$/.test(val)) {
      fetchAddressFromPincode(val);
    } else {
      setDistrict("");
      setState("");
      setCountry("");
      setError("");
    }
  };

  const handleSave = async () => {
    if (!address || !pincode || !district || !state || !country) {
      setError("Please fill in all fields.");
      return;
    }

    try {
      const payload = {
        address,
        pincode,
        city: district,
        state,
        country,
      };

      await axios.put(`${API_URL}/api/user/address/update/?user_id=${userId}`, payload);
      setEditing(false);
      setError("");
      setSuccessMsg("Address saved successfully!");

      if (onUpdate) onUpdate(); // reload parent only after save
    } catch (err) {
      setError("Failed to save address.");
    }
  };

  if (isLoading)
    return (
      <Box
                                      sx={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        width: '100vw',
                                        height: '100vh',
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        zIndex: 2000,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backdropFilter: 'blur(3px)',
                                      }}
                                    >
                                      <Box sx={{ textAlign: 'center' }}>
                                        <CircularProgress
                            size={60}
                            thickness={6}
                            sx={{
                              color: 'transparent',
                              '& .MuiCircularProgress-circle': {
                                stroke: 'url(#better-gradient)',
                              },
                            }}
                          />
                          
                          <svg width="0" height="0">
                            <defs>
                              <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#f30b96ff" />     {/* pinkish-purple */}
                                <stop offset="50%" stopColor="#221ce0ff" />     {/* deep lavender */}
                                <stop offset="100%" stopColor="#0eacebff" />    {/* bright sky blue */}
                              </linearGradient>
                            </defs>
                          </svg>
                          
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            mt: 2,
                                            color: '#fff',
                                            textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                                          }}
                                        >
                                          Loading Address Details... ({loadingSeconds} sec)
                                        </Typography>
                                      </Box>
                                    </Box>
    );

  return (
    <Fade in>
      <Paper
        elevation={6}
        sx={{
          maxWidth: 520,
          mx: "auto",
          mt: 5,
          p: 4,
          bgcolor: "#0d1117",
          color: "#f5f5f5",
          borderRadius: 3,
          // boxShadow:
          //   "0px 6px 15px rgba(0,0,0,0.8), 0px 0px 6px rgba(0, 0, 0, 0.5)",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          fontWeight={600}
          align="center"
        >
          {editing ? "Enter Your Address" : "Address Details"}
        </Typography>

        <Divider sx={{ mb: 3, borderColor: "#30363d" }} />
        <Stack spacing={3}>
          {editing ? (
            <>
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                multiline
                rows={2}
                variant="filled"
                sx={inputStyles}
              />
              <TextField
                label="Pincode"
                value={pincode}
                onChange={handlePincodeChange}
                fullWidth
                error={!!error && error.toLowerCase().includes("pincode")}
                helperText={error.toLowerCase().includes("pincode") ? error : ""}
                variant="filled"
                InputProps={{
                  endAdornment: fetchingPinData && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
                sx={inputStyles}
              />
              <TextField
                label="District"
                value={district}
                fullWidth
                disabled
                variant="filled"
                sx={disabledInputStyles}
              />
              <TextField
                label="State"
                value={state}
                fullWidth
                disabled
                variant="filled"
                sx={disabledInputStyles}
              />
              <TextField
                label="Country"
                value={country}
                fullWidth
                disabled
                variant="filled"
                sx={disabledInputStyles}
              />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  !address ||
                  !pincode ||
                  !district ||
                  !state ||
                  !country ||
                  !!error
                }
                size="large"
                sx={saveButtonStyles}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <InfoRow label="Address" value={address} />
              <InfoRow label="Pincode" value={pincode} />
              <InfoRow label="District" value={district} />
              <InfoRow label="State" value={state} />
              <InfoRow label="Country" value={country} />
              <Button
                variant="outlined"
                onClick={() => setEditing(true)}
                startIcon={<LocationOnIcon />}
                sx={editButtonStyles}
                size="large"
              >
                Edit
              </Button>
            </>
          )}
        </Stack>

        {!!error && !error.toLowerCase().includes("pincode") && (
          <Typography color="error" mt={2}>
            {error}
          </Typography>
        )}

        <Snackbar
          open={!!successMsg}
          autoHideDuration={3000}
          onClose={() => setSuccessMsg("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            sx={{ width: "100%", bgcolor: "#388e3c", color: "#fff" }}
            onClose={() => setSuccessMsg("")}
          >
            {successMsg}
          </Alert>
        </Snackbar>
      </Paper>
    </Fade>
  );
}

const inputStyles = {
  backgroundColor: "#111111",
  borderRadius: 1,
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
    color: "#ffffff",
  },
  "& .MuiInputLabel-root": {
    color: "#ffffffff",
  },
  "& .MuiFilledInput-root": {
    backgroundColor: "#1e1e1e",
    borderRadius: 1,
    "&:hover": {
      backgroundColor: "#2c2c2c",
    },
    "&.Mui-focused": {
      backgroundColor: "#1e1e1e",
    },
  },
};

const disabledInputStyles = {
  ...inputStyles,
  "& .MuiFilledInput-root.Mui-disabled": {
    backgroundColor: "#1e1e1e",
    color: "#ffffff",
    "-webkit-text-fill-color": "#ffffff",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#ffffff",
    "-webkit-text-fill-color": "#ffffff",
  },
};

const saveButtonStyles = {
  bgcolor: "#1E90FF",
  color: "#fff",
  fontWeight: 700,
  "&:hover": {
    bgcolor: "#1c86ee",
  },
};

const editButtonStyles = {
  mt: 3,
  color: "#1E90FF",
  borderColor: "#1E90FF",
  fontWeight: 600,
  "&:hover": {
    bgcolor: "rgba(30, 144, 255, 0.1)",
  },
};

const InfoRow = ({ label, value }) => (
  <Typography>
    <strong>{label}:</strong> {value || "-"}
  </Typography>
);

export default AddressByPincode;
